import React from 'react';
import PropTypes from 'prop-types';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { injectTestId } from '@cvent/nucleus-test-automation';
import {
  CheckBoxGroupPrimitive,
  CheckBoxItemWrapper
} from './CheckBoxGroupPrimitives';
import CheckBox from './CheckBox';

/**
 * CheckBoxGroup control with no surrounding label and error/validation message
 * functionality for React. CheckBoxGroup will pass its props into the CheckBox children. */
export default class CheckBoxGroup extends React.Component {
  static propTypes = {
    /** Name for the CheckBoxGroup -- each CheckBox instance will have this name. */
    name: PropTypes.string.isRequired,

    /** Checkbox instances. When `children` are provided, state of each child must be managed individually:
     * `selected` and `onUpdate` props may not be used. The `options` prop is the preferred way of creating checkboxes. */
    children: PropTypes.node,

    /** CheckBoxGroup will set all CheckBox instances to the disabled mode. */
    disabled: PropTypes.bool,

    /** Label applied to the legend element for web checkbox groups. */
    groupLabel: PropTypes.string,

    /** Sets the CheckBoxGroup into an error state and is passed to each CheckBox instance. */
    hasError: PropTypes.bool,

    /** CheckBoxGroup will display CheckBox instances in a row instead of block (vertically). */
    inline: PropTypes.bool,

    /** Text direction will be set for all CheckBox instances */
    isRtl: PropTypes.bool,

    /** When a checkbox is checked or unchecked, the callback provided to `onUpdate` will be called,
     * suppling an argument containing the selection array.
     * `onUpdate` is only used when checkBoxes are created via the `options` prop.
     * If `CheckBox` elements are supplied as children, the `onUpdate` function will not be called.
     * @param {array} selectionValues : an array with the selected values: is usually passed back to `selected` prop (via state)
     * @param {object} option : option object*/
    onUpdate: PropTypes.func,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    /** Array of option objects to be rendered as CheckBoxes. In most cases, this is the preferred way to create checkboxes (as opposed to using `children`) */
    options: PropTypes.arrayOf(
      PropTypes.shape({
        /** Each 'value' must be unique: Options with duplicate values will be removed! 'value' is the only property searched with the default filter.
         */
        value: PropTypes.string.isRequired,
        /** 'label' value is rendered when 'option' is selected, and also in the menu. If no 'label' is provided, the 'value' will be copied to 'label'. A render-props component can also be provided for custom rendering, 'theme' will be passed back as an argument.
         */
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        /** Display disabled styling for 'option', remove interactivity (cannot be selected). */
        disabled: PropTypes.bool
      })
    ),

    /** Array containing values for each checked CheckBox. Should usually be passed back by `onUpdate` function */
    selected: PropTypes.array
  };

  static defaultProps = {
    hasError: false,
    inline: false,
    disabled: false,
    isRtl: false
  };

  render() {
    const {
      hasError,
      inline,
      disabled,
      isRtl,
      name,
      children,
      testID,
      options,
      ...restProps
    } = this.props;
    return (
      <ThemeConsumer>
        {theme => (
          <CheckBoxGroupPrimitive
            {...restProps}
            {...{ theme, isRtl, inline }}
            {...injectTestId(testID)}
          >
            {this.checkBoxesFromChildrenOrOptions(theme)}
          </CheckBoxGroupPrimitive>
        )}
      </ThemeConsumer>
    );
  }

  checkBoxesFromChildrenOrOptions = theme => {
    const {
      hasError,
      inline,
      disabled,
      isRtl,
      name,
      children,
      options,
      selected,
      /* eslint-disable react/prop-types */
      // standard attributes
      required
      /* eslint-enable react/prop-types */
    } = this.props;

    const uniqueId = value => `${name + '_'}${value}`;

    if (children) {
      return React.Children.map(children, checkBox => {
        return (
          <CheckBoxItemWrapper
            {...{ inline, theme, isRtl }}
            key={uniqueId(checkBox.props.value)}
          >
            {
              <checkBox.type
                {...checkBox.props}
                name={name}
                hasError={hasError}
                disabled={disabled}
                isRtl={isRtl}
                id={uniqueId(checkBox.props.value)}
              />
            }
          </CheckBoxItemWrapper>
        );
      });
    } else if (options) {
      return options.map(checkBox => (
        <CheckBoxItemWrapper
          {...{ inline, theme, isRtl }}
          key={uniqueId(checkBox.value)}
        >
          {
            <CheckBox
              value={checkBox.value}
              name={name}
              hasError={hasError}
              disabled={checkBox.disabled || disabled}
              isRtl={isRtl}
              required={required}
              checked={selected && selected.includes(checkBox.value)}
              onChange={this.handleCheckBoxChange}
              id={uniqueId(checkBox.value)}
            >
              {checkBox.label || checkBox.value}
            </CheckBox>
          }
        </CheckBoxItemWrapper>
      ));
    }
  };

  handleCheckBoxChange = ({ target }) => {
    const selected = this.props.selected || [];
    const { checked, value } = target;

    // return updated array to onUpdate function (prop)
    if (checked && !selected.includes(value)) {
      return this.props.onUpdate(selected.concat([value]));
    } else if (!checked && selected.includes(value)) {
      return this.props.onUpdate(
        selected.filter(currentValue => currentValue !== value)
      );
    }
  };
}
